// Study Hall moderator drawer (DESIGN.md 8.4, 8.6, 8.10, 12): Queue, Flags, Audit and Room tabs,
// the user card, and action dialogs that always carry a reason (one click on a preset, or 3+
// typed characters). Only mounted for staff (or a browser holding the forum owner key). Everything
// goes through the `{hall}a/mod/*` and `{hall}a/owner/*` routes; the server re-checks staff and
// hierarchy on every call, so this is convenience, never the gate. Response shapes are read
// defensively: a missing field renders as nothing rather than breaking the drawer.
//
// Built for a very small team (one moderator at launch): urgent items sort first and are marked,
// a modq push while the drawer is open reloads the queue, and every reason is one click away.
import * as C from "./copy.js";
import { h, icon, fmtFull, badgeFor } from "./view.js";
import { modal } from "./dialogs.js";

const M = C.MOD;
const arr = (v) => (Array.isArray(v) ? v : []);
const obj = (v) => (v && typeof v === "object" && !Array.isArray(v) ? v : {});
const s = (v) => (typeof v === "string" ? v : v === undefined || v === null ? "" : String(v));
const SLOWS = [0, 3, 5, 10, 30, 60, 300];
const LOCKDOWN_MINUTES = [[15, "15 minutes"], [60, "1 hour"], [240, "4 hours"], [1440, "24 hours"]];

// The categories on a queue item: a list, a {cat: count} map, or the reports' own.
export function catsOf(it, reports) {
	if (Array.isArray(it.cats)) return it.cats.map(s).filter(Boolean);
	if (it.cats && typeof it.cats === "object") return Object.keys(it.cats);
	return [...new Set(reports.map((r) => s(r.cat)).filter(Boolean))];
}

// One queue item, normalized from either the live staff row (`message`, shape M) or the report
// snapshot (`snapshot.msg`, flat {id, u, h, b, st, revs}).
export function queueItem(it) {
	const live = obj(it.message || it.m);
	const snap = obj(it.snapshot);
	const sm = obj(snap.msg || it.msg);
	const la = obj(live.a);
	const reports = arr(it.reports);
	const author = obj(it.author);
	return {
		msgId: Number(it.msgId || live.id || sm.id) || 0,
		reportId: Number(it.reportId || (reports[0] && reports[0].id)) || 0,
		room: s(it.room || live.room || sm.room),
		urgent: !!it.urgent,
		count: Number(it.total || it.count) || reports.length || 1,
		cats: catsOf(it, reports),
		u: s(author.u || la.u || sm.u),
		h: s(author.h || la.h || sm.h),
		st: Number(live.st !== undefined ? live.st : sm.st) || 0,
		b: s(sm.b !== undefined ? sm.b : live.b),
		revs: arr(live.revisions || live.revs || sm.revs),
		before: arr(snap.before),
		after: arr(snap.after),
		reports,
		author,
	};
}

// The queue items of a `mod/queue` answer, normalized (the list may come under a few names).
export function queueItems(j) {
	const o = obj(j);
	return arr(Array.isArray(j) ? j : o.items || o.queue || o.groups || o.reports).map(queueItem);
}

// "13-17 · on probation · joined 2026-09 · 2 sanctions · strikes: spam 2" (never a sub).
export function authorLine(a) {
	const out = [];
	if (a.age) out.push(s(a.age));
	if (a.probation) out.push(M.probation.toLowerCase());
	if (a.trusted) out.push(M.trusted.toLowerCase());
	if (a.joined) out.push(M.joined.toLowerCase() + " " + s(a.joined));
	if (Number(a.sanctions)) out.push(M.sanctions.toLowerCase() + ": " + Number(a.sanctions));
	const st = obj(a.strikes);
	const sk = Object.entries(st).filter(([, n]) => Number(n) > 0).map(([k, n]) => k + " " + n);
	if (sk.length) out.push(M.strikes.toLowerCase() + ": " + sk.join(", "));
	return out.join(" · ");
}

export function createMod({ api, store, owner, toast }) {
	const S = store.S;
	let root = null, body = null, tab = "queue", qstatus = "open", tabs = {}, refreshT = 0, loading = false, again = false, opener = null;

	function mount(el) {
		root = el;
		root.textContent = "";
		tabs = {};
		const bar = h("div", { class: "rv-hall-mtabs", role: "tablist" });
		for (const [id, label] of [["queue", M.queue], ["flags", M.flags], ["audit", M.audit], ["room", M.room]]) {
			tabs[id] = h("button", { type: "button", role: "tab", id: "rv-hall-mtab-" + id, "aria-controls": "rv-hall-mbody", class: "rv-hall-mtab", text: label, "aria-selected": id === tab ? "true" : "false", onclick: () => { tab = id; paintTabs(); load(); } });
			bar.append(tabs[id]);
		}
		// Arrow keys, Home and End move between the tabs (one Tab stop for the whole row).
		bar.addEventListener("keydown", (e) => {
			const ids = Object.keys(tabs), i = ids.indexOf(tab);
			const to = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: ids.length - 1 }[e.key];
			if (to == null) return;
			e.preventDefault();
			tab = ids[(to + ids.length) % ids.length]; paintTabs(); load(); tabs[tab].focus();
		});
		paintTabs();
		body = h("div", { class: "rv-hall-mbody", id: "rv-hall-mbody", role: "tabpanel" });
		root.setAttribute("role", "region");
		root.setAttribute("aria-labelledby", "rv-hall-mtitle");
		root.addEventListener("keydown", (e) => { if (e.key === "Escape" && !e.defaultPrevented) { e.preventDefault(); close(); } });
		root.append(h("div", { class: "rv-hall-mhead" }, h("h2", { id: "rv-hall-mtitle", text: M.title }), h("button", { type: "button", class: "rv-hall-icon-btn", "aria-label": C.CLOSE, title: C.CLOSE, onclick: close }, icon("close"))), bar, body);
	}
	// Each tab says how much is waiting in it (from the server's modq counts), so the one moderator
	// sees where the shield badge's number lives: reports in Queue, support flags and alerts in Flags.
	function paintTabs() {
		const q = S.modq || {};
		const n = { queue: Number(q.reports) || 0, flags: (Number(q.support) || 0) + (Number(q.alerts) || 0) };
		for (const [id, b] of Object.entries(tabs)) {
			b.setAttribute("aria-selected", id === tab ? "true" : "false");
			b.tabIndex = id === tab ? 0 : -1;
			if (body && id === tab) body.setAttribute("aria-labelledby", b.id);
			const base = { queue: M.queue, flags: M.flags, audit: M.audit, room: M.room }[id];
			b.textContent = n[id] ? base + " (" + (n[id] > 99 ? "99+" : n[id]) + ")" : base;
		}
	}
	const isOpen = () => !!root && !root.hidden && root.isConnected;
	function toggle(el) {
		if (isOpen() && root === el) { close(); return; }
		// Opening from the badge: land where the waiting items are (reports first) unless the last tab has some.
		const q = S.modq || {}, nq = Number(q.reports) || 0, nf = (Number(q.support) || 0) + (Number(q.alerts) || 0);
		if (!(tab === "queue" && nq) && !(tab === "flags" && nf) && (nq || nf)) tab = nq ? "queue" : "flags";
		if (root !== el || !body) mount(el);
		else paintTabs();
		const ae = document.activeElement;
		if (ae && ae !== document.body && !root.contains(ae)) opener = ae;
		root.hidden = false;
		if (body) body.setAttribute("aria-labelledby", "rv-hall-mtab-" + tab);
		if (tabs[tab]) tabs[tab].focus();
		load();
	}
	function close() {
		if (!root) return;
		const inside = root.contains(document.activeElement);
		root.hidden = true;
		if (inside && opener && opener.isConnected) { try { opener.focus(); } catch (e) { void e; } }
	}
	// A modq push while open: reload the queue shortly (coalesced).
	function poke() {
		if (root && tabs.queue) paintTabs();
		if (!isOpen() || (tab !== "queue" && tab !== "flags") || refreshT) return;
		// Only if still on a list: a refresh that lands after a switch to Room would repaint it under the owner.
		refreshT = setTimeout(() => { refreshT = 0; if (isOpen() && (tab === "queue" || tab === "flags")) load(); }, 1500);
	}

	async function load() {
		if (!body) return;
		if (loading) { again = true; return; }
		loading = true;
		body.textContent = "";
		body.append(h("p", { class: "rv-hall-muted", text: M.load }));
		try {
			if (tab === "queue") paintQueue(await api.authed("mod/queue?status=" + qstatus));
			else if (tab === "flags") paintFlags(await api.authed("mod/flags?kind=support"), await api.authed("mod/flags?kind=alert"), await api.authed("mod/flags?kind=blocked"), await api.authed("mod/attempts"));
			else if (tab === "audit") paintAudit(await api.authed("mod/audit?limit=100"));
			else paintRoom();
		} catch (e) {
			body.textContent = "";
			body.append(h("p", { class: "rv-hall-dlg-err", text: e && e.message ? e.message : M.failed }));
		} finally {
			loading = false;
			if (again) { again = false; load(); }
		}
	}

	// --- queue ------------------------------------------------------------------------------------
	const lineOf = (r, hl) => h("div", { class: "rv-hall-ctxline" + (hl ? " is-hl" : "") }, h("b", { text: s(r.h || obj(r.a).h) + ": " }), h("span", { text: s(r.b) || C.ST_LABEL[r.st] || "" }));
	function paintQueue(j) {
		body.textContent = "";
		const seg = h("div", { class: "rv-hall-mrow" },
			h("button", { type: "button", class: "rv-btn" + (qstatus === "open" ? " rv-btn-primary" : ""), text: M.openQ, onclick: () => { qstatus = "open"; load(); } }),
			h("button", { type: "button", class: "rv-btn" + (qstatus === "recent" ? " rv-btn-primary" : ""), text: M.recent, onclick: () => { qstatus = "recent"; load(); } }));
		body.append(seg);
		const f = obj(j.flags);
		const wd = obj(j.words);
		if (Object.keys(f).length) body.append(h("p", { class: "rv-hall-muted", text: M.support + ": " + (Number(f.support) || 0) + " · " + M.blocked + ": " + (Number(f.blocked) || 0) + (Object.keys(wd).length ? " · " + M.words(Number(wd.total) || 0) : "") }));
		const items = queueItems(j);
		if (!items.length) { body.append(h("p", { class: "rv-hall-muted", text: M.empty })); return; }
		for (const it of items) {
			const card = h("article", { class: "rv-hall-mcard" + (it.urgent ? " is-urgent" : "") },
				h("div", { class: "rv-hall-mcard-head" },
					it.urgent ? h("span", { class: "rv-hall-badge", "data-s": "urgent", text: M.urgent }) : null,
					h("span", { text: "#" + it.room + " · " + M.reports(it.count) + (it.cats.length ? " · " + it.cats.join(", ") : "") + (it.st ? " · " + (C.ST_LABEL[it.st] || "") : "") })),
				it.author && authorLine(it.author) ? h("p", { class: "rv-hall-muted", text: it.h + ": " + authorLine(it.author) }) : null,
				h("div", { class: "rv-hall-mctx" }, it.before.map((r) => lineOf(r)), lineOf({ h: it.h, b: it.b, st: it.st }, true), it.after.map((r) => lineOf(r))));
			for (const r of it.revs) card.append(h("p", { class: "rv-hall-muted", text: M.rev + " " + s(r.rev) + ": " + s(r.b) }));
			for (const r of it.reports) if (r.note) card.append(h("p", { class: "rv-hall-muted", text: s(r.cat) + ": " + s(r.note) }));
			const target = { msgId: it.msgId, reportId: it.reportId, u: it.u, h: it.h };
			const acts = h("div", { class: "rv-hall-macts" });
			const btn = (label, fn, danger) => acts.append(h("button", { type: "button", class: "rv-btn" + (danger ? " rv-btn-danger" : ""), text: label, onclick: fn }));
			if (qstatus === "open") {
				if (it.st !== 2) btn(C.T.remove, () => act("remove", target), true);
				btn(M.dismiss, () => act("dismiss", target));
			}
			if (it.st === 3 || it.st === 2) btn(C.T.restore, () => act("restore", target));
			if (it.msgId) btn(M.context, () => context(it.msgId));
			if (it.u) { btn(C.T.timeout, () => act("timeout", target)); btn(C.T.ban, () => act("ban", target), true); btn(C.T.user, () => userCard(it.u)); }
			if (owner() && it.reportId) { btn(M.escalate, () => act("escalate", target), true); btn(M.escalation, () => escalation(it.reportId)); }
			card.append(acts);
			body.append(card);
		}
	}

	async function context(msgId) {
		let j;
		try { j = await api.authed("mod/ctx/" + encodeURIComponent(msgId) + "?n=10"); } catch (e) { toast(e && e.message ? e.message : M.failed); return; }
		const m = obj(j.m || j.msg || j.message);
		modal({
			title: M.context, cls: "rv-hall-dlg-wide",
			body: [
				h("div", { class: "rv-hall-mctx" }, arr(j.before).map((r) => lineOf(r)), lineOf({ ...m, h: s(obj(m.a).h || m.h) }, true), arr(j.after).map((r) => lineOf(r))),
				arr(m.revisions || m.revs).map((r) => h("p", { class: "rv-hall-muted", text: M.rev + " " + s(r.rev) + ": " + s(r.b) })),
			],
			actions: [{ label: C.CLOSE, primary: true, run: (a) => a.close() }],
		});
	}

	function paintFlags(sup, alerts, blk, att) {
		body.textContent = "";
		// Sexual-word attempts last: records with nothing to resolve.
		for (const [label, j, kind] of [[M.support, sup, "support"], [M.alerts, alerts, "alert"], [M.blocked, blk, "blocked"], [M.attempts, att]]) {
			body.append(h("h3", { class: "rv-hall-mh3", text: label }));
			const items = Array.isArray(j) ? j : arr(obj(j).items || obj(j).flags || obj(j).attempts);
			if (!items.length) { body.append(h("p", { class: "rv-hall-muted", text: M.empty })); continue; }
			for (const f of items) {
				const what = kind === "alert" ? M.alertCats[s(f.cat)] || s(f.cat) : s(f.cat);
				const where = [what, f.room ? "#" + s(f.room) : "", s(f.h), f.at ? fmtFull(f.at) : ""].filter(Boolean).join(" · ");
				body.append(h("article", { class: "rv-hall-mcard" + (f.urgent ? " is-urgent" : "") },
					h("div", { class: "rv-hall-mcard-head" }, f.urgent ? h("span", { class: "rv-hall-badge", "data-s": "urgent", text: M.urgent }) : null, h("span", { text: where })),
					f.b ? h("p", { class: "rv-hall-mquote", text: s(f.b) }) : null,
					f.held ? h("p", { class: "rv-hall-muted", text: M.heldHint }) : null,
					h("div", { class: "rv-hall-macts" },
						!kind ? null : f.held && f.msgId
							? [h("button", { type: "button", class: "rv-btn rv-btn-primary", text: C.T.approve, onclick: () => approve(f.msgId) }),
								h("button", { type: "button", class: "rv-btn", text: C.T.remove, onclick: () => act("remove", { msgId: f.msgId, h: s(f.h) }) })]
							: h("button", { type: "button", class: "rv-btn", text: M.resolve, onclick: () => post("mod/act", { verb: "resolve_flag", flagId: f.id, reason: "Reviewed" }).then((ok) => { if (ok) load(); }) }),
						f.msgId ? h("button", { type: "button", class: "rv-btn", text: M.context, onclick: () => context(f.msgId) }) : null,
						f.u ? h("button", { type: "button", class: "rv-btn", text: C.T.user, onclick: () => userCard(f.u) }) : null)));
			}
		}
	}

	function paintAudit(j) {
		body.textContent = "";
		const items = Array.isArray(j) ? j : arr(obj(j).entries || obj(j).items || obj(j).audit);
		if (!items.length) { body.append(h("p", { class: "rv-hall-muted", text: M.auditEmpty })); return; }
		const ul = h("ul", { class: "rv-hall-audit" });
		for (const e of items) {
			const t = obj(e.target);
			ul.append(h("li", null, h("time", { text: e.at ? fmtFull(e.at) : "" }), h("span", { text: " " + s(e.action).replace(/^chat\./, "") + " · " + s(e.actor) + (t.handle ? " > " + s(t.handle) : "") + (t.room ? " #" + s(t.room) : "") + (e.detail ? ": " + s(e.detail) : "") })));
		}
		body.append(ul);
	}

	// --- room controls, lockdown, mode ---------------------------------------------------------------
	function paintRoom() {
		body.textContent = "";
		const r = store.room(S.focus);
		if (r) {
			const slow = h("select", { class: "rv-hall-select", "aria-label": M.slow }, SLOWS.map((n) => h("option", { value: String(n), text: n ? C.SLOW(n) : M.slow + ": " + M.off })));
			slow.value = String(r.slow || 0);
			body.append(h("h3", { class: "rv-hall-mh3", text: "#" + r.name }),
				h("div", { class: "rv-hall-mrow" }, slow, h("button", { type: "button", class: "rv-btn", text: M.apply, onclick: () => post("mod/room", { room: r.id, slow: Number(slow.value) }) })),
				// The server's `room` event may land before this answer: set the wanted state, never toggle twice.
				h("div", { class: "rv-hall-mrow" }, h("button", { type: "button", class: "rv-btn", text: r.locked ? M.unlock : M.lock, onclick: () => { const want = !r.locked; post("mod/room", { room: r.id, locked: want }).then((ok) => { if (ok) { r.locked = want; paintRoom(); } }); } })));
			if (owner()) {
				const topic = h("input", { class: "rv-hall-field", type: "text", maxlength: "160", value: r.topic, "aria-label": M.topic });
				body.append(h("div", { class: "rv-hall-mrow" }, topic, h("button", { type: "button", class: "rv-btn", text: M.setTopic, onclick: () => post("mod/room", { room: r.id, topic: topic.value.trim() }) })));
			}
			// A question of the day: a normal staff message, pinned in a subject room for a day.
			if (r.subject) {
				const q = h("textarea", { class: "rv-hall-field", maxlength: "300", rows: "2", "aria-label": C.QOTD, "aria-describedby": "rv-hall-qotd-hint" });
				body.append(h("h3", { class: "rv-hall-mh3", text: C.QOTD }), q, h("p", { class: "rv-hall-muted", id: "rv-hall-qotd-hint", text: C.QOTD_HINT }),
					h("div", { class: "rv-hall-mrow" }, h("button", { type: "button", class: "rv-btn", text: C.QOTD_POST, onclick: () => { const text = q.value.trim(); if (text) post("mod/qotd", { room: r.id, text }).then((ok) => { if (ok) q.value = ""; }); } })));
			}
		}
		body.append(invitesBox(r));
		const lv = h("select", { class: "rv-hall-select", "aria-label": M.lockdown }, M.level.map((t, i) => h("option", { value: String(i), text: t })));
		lv.value = String(store.lockdownLevel());
		const mins = h("select", { class: "rv-hall-select", "aria-label": M.minutes }, LOCKDOWN_MINUTES.map(([v, t]) => h("option", { value: String(v), text: t })));
		const ld = S.lockdown || {};
		body.append(h("h3", { class: "rv-hall-mh3", text: M.lockdown }));
		if (ld.level) body.append(h("p", { class: "rv-hall-muted", text: M.level[ld.level] + (ld.until ? " · " + fmtFull(ld.until) : "") + (ld.quiet ? " · " + M.quiet : "") }));
		body.append(h("div", { class: "rv-hall-mrow" }, lv, mins, h("button", { type: "button", class: "rv-btn", text: M.apply, onclick: () => post("mod/lockdown", { level: Number(lv.value), minutes: Number(mins.value) }) })));
		if (owner()) {
			const mode = h("select", { class: "rv-hall-select", "aria-label": M.mode }, M.modes.map((m) => h("option", { value: m, text: M.modeNames[m] || m })));

			mode.value = S.mode;
			body.append(h("h3", { class: "rv-hall-mh3", text: M.mode }), h("div", { class: "rv-hall-mrow" }, mode, h("button", { type: "button", class: "rv-btn", text: M.apply, onclick: () => post("owner/mode", { mode: mode.value }) })));
			body.append(betaBox());
		}
	}

	// Invite links: this room and all of Study Hall, with the last day's totals (hall/invite.js).
	function invitesBox(r) {
		const box = h("div", {});
		import(__RVM.hallInviteJs).then((m) => m.modBox(box, { r, api, h, post, again: () => { if (isOpen() && tab === "room") paintRoom(); } })).catch(() => {});
		return box;
	}

	// Owner only: who is in the beta, by public name. The server holds the cap.
	function betaBox() {
		const count = h("p", { class: "rv-hall-muted", id: "rv-hall-beta-count", "aria-live": "polite" });
		const list = h("ul", { class: "rv-hall-mlist", "aria-labelledby": "rv-hall-beta-h" });
		const name = h("input", { class: "rv-hall-field", type: "text", maxlength: "32", autocomplete: "off", "aria-label": M.betaName, "aria-describedby": "rv-hall-beta-hint" });
		async function refresh() {
			let j;
			try { j = await api.authed("owner/beta"); } catch (e) { count.textContent = M.failed; return; }
			count.textContent = M.betaCount(j.total, j.cap);
			list.textContent = "";
			const rows = (j.members || []).filter((m) => m.h).sort((a, b) => a.h.localeCompare(b.h));
			if (!rows.length) list.append(h("li", { class: "rv-hall-muted", text: M.betaEmpty }));
			for (const m of rows) {
				list.append(h("li", { class: "rv-hall-mrow" }, h("span", { text: m.h }),
					h("button", { type: "button", class: "rv-btn", text: M.betaRemoveShort, "aria-label": M.betaRemove(m.h), onclick: () => post("owner/beta", { handle: m.h, on: false }).then((ok) => { if (ok) refresh(); }) })));
			}
		}
		const add = () => { const v = name.value.trim(); if (v) post("owner/beta", { handle: v, on: true }).then((ok) => { if (ok) { name.value = ""; refresh(); } }); };
		name.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); add(); } });
		refresh();
		return h("section", { class: "rv-hall-mbeta" },
			h("h3", { class: "rv-hall-mh3", id: "rv-hall-beta-h", text: M.beta }), count,
			h("p", { class: "rv-hall-muted", id: "rv-hall-beta-hint", text: M.betaHint }),
			h("div", { class: "rv-hall-mrow" }, name, h("button", { type: "button", class: "rv-btn", text: M.betaAdd, onclick: add })),
			list);
	}

	async function post(path, bodyObj) {
		try { await api.authed(path, { method: "POST", body: bodyObj }); toast(M.done); if (isOpen() && tab !== "room") load(); return true; }
		catch (e) { toast(e && e.message ? e.message : M.failed); return false; }
	}

	// Approving a held message needs no reason picker: it just goes out.
	function approve(msgId) { return post("mod/act", { verb: "approve", msgId, reason: M.approved }); }

	// --- actions with a reason -------------------------------------------------------------------
	// target: {msgId?, reportId?, u?, h?, on?}. verb as in 8.4. A preset reason applies at once.
	function act(verb, target) {
		const extra = [];
		let minutes = null, hours = null, everywhere = null;
		if (verb === "timeout") { minutes = h("select", { class: "rv-hall-select", "aria-label": M.minutes }, M.durations.map(([v, t]) => h("option", { value: String(v), text: t }))); minutes.value = "60"; extra.push(minutes); }
		if (verb === "purge") { hours = h("select", { class: "rv-hall-select", "aria-label": M.purge }, M.purgeHours.map(([v, t]) => h("option", { value: String(v), text: t }))); extra.push(hours); }
		if (verb === "ban") { everywhere = h("input", { type: "checkbox" }); everywhere.checked = true; extra.push(h("label", { class: "rv-hall-check" }, everywhere, h("span", { text: M.everywhere }))); }
		const free = h("input", { class: "rv-hall-field", type: "text", maxlength: "200", placeholder: M.reasonHint, "aria-label": M.reasonHint });
		const title = (C.T[verb] || M[verb] || verb) + (target.h ? ": " + target.h : "");
		let dlg = null;
		async function go(reason) {
			if (reason.length < 3) { dlg.error(M.reasonHint); return; }
			const b = { verb, reason };
			if (target.msgId) b.msgId = target.msgId;
			if (target.reportId) b.reportId = target.reportId;
			if (target.u) b.u = target.u;
			if (minutes) b.minutes = Number(minutes.value);
			if (hours) b.hours = Number(hours.value);
			if (everywhere) b.everywhere = everywhere.checked;
			if (verb === "trust") b.on = !!target.on;
			dlg.busy(true);
			if (await post("mod/act", b)) dlg.close(); else dlg.busy(false);
		}
		const presets = h("div", { class: "rv-hall-presets", role: "group", "aria-label": M.reason }, M.presets.map((p) => h("button", { type: "button", class: "rv-btn", text: p, onclick: () => go(p) })));
		dlg = modal({
			title, body: [extra, h("p", { class: "rv-hall-muted", text: M.reason }), presets, free],
			actions: [{ label: C.CANCEL, run: (a) => a.close() }, {
				label: M.apply, primary: verb !== "ban" && verb !== "remove", danger: verb === "ban" || verb === "remove",
				run: () => go(free.value.trim()),
			}],
		});
		free.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); go(free.value.trim()); } });
	}

	// --- user card -------------------------------------------------------------------------------
	async function userCard(u) {
		let j;
		try { j = await api.authed("mod/user/" + encodeURIComponent(u)); } catch (e) { toast(e && e.message ? e.message : M.failed); return; }
		j = obj(j);
		const hd = s(j.h || j.handle);
		const cur = obj(j.current);
		const rows = [
			["u", s(j.u || u)],
			[M.age, s(j.age || j.ageTag)],
			[M.joined, s(j.joined || j.joinMonth)],
			[M.probation, j.probation ? M.yes : M.no],
			[M.trusted, j.trusted ? M.yes : M.no],
			[M.messageCount, typeof j.messages === "number" ? String(j.messages) : ""],
			[M.current, cur.kind ? s(cur.kind) + (cur.until ? " · " + fmtFull(cur.until) : "") + (cur.reason ? ": " + s(cur.reason) : "") : ""],
			[M.strikes, Object.entries(obj(j.strikes)).map(([k, v]) => k + " " + v).join(", ")],
		];
		const hist = arr(j.handles || j.history || j.handleHistory).map((x) => s(x.old) + " > " + s(x.new) + (x.at ? " (" + fmtFull(x.at) + ")" : ""));
		const sanctions = arr(j.sanctions).map((x) => s(x.kind) + ": " + s(x.reason) + (x.until ? " (" + fmtFull(x.until) + ")" : "") + (x.by ? " · " + s(x.by) : "") + (x.lifted ? " · " + M.lifted : ""));
		const tried = arr(j.attempts).map((a) => fmtFull(a.at) + " #" + s(a.room) + " " + s(a.b));
		const recent = arr(j.recent || (Array.isArray(j.messages) ? j.messages : [])).slice(0, 50).map((m) => "#" + s(m.room) + " " + (s(m.b) || C.ST_LABEL[m.st] || ""));
		const list = (label, items) => (items.length ? [h("h3", { class: "rv-hall-mh3", text: label }), h("ul", { class: "rv-hall-mlist" }, items.map((t) => h("li", { text: t })))] : []);
		const tgt = { u, h: hd };
		const acts = [
			[C.T.timeout, () => act("timeout", tgt)], [M.untimeout, () => act("untimeout", tgt)],
			[C.T.ban, () => act("ban", tgt)], [M.unban, () => act("unban", tgt)],
			[M.purge, () => act("purge", tgt)],
			[j.trusted ? M.untrust : M.trust, () => act("trust", { ...tgt, on: !j.trusted })],
		];
		if (owner()) {
			acts.push([Number(j.staff) === 1 ? M.revoke : M.grant, () => post("owner/mods", { u, on: Number(j.staff) !== 1 })]);
			acts.push([M.ageReset, () => post("owner/age-reset", { u })]);
		}
		const badge = badgeFor(Number(j.staff) || 0);
		modal({
			title: hd + (badge ? " (" + badge + ")" : ""), cls: "rv-hall-dlg-wide",
			body: [
				h("dl", { class: "rv-hall-dl" }, rows.filter((r) => r[1]).map(([k, v]) => [h("dt", { text: k }), h("dd", { text: v })])),
				list(M.history, hist), list(M.sanctions, sanctions), list(M.attempts, tried), list(M.messages, recent),
				h("div", { class: "rv-hall-macts" }, acts.map(([label, fn]) => h("button", { type: "button", class: "rv-btn", text: label, onclick: fn }))),
			],
			actions: [{ label: C.CLOSE, run: (a) => a.close() }],
		});
	}

	// --- escalation (owner only; the only view that shows a sub) -------------------------------------
	async function escalation(reportId) {
		let j;
		try { j = await api.authed("owner/escalation/" + encodeURIComponent(reportId)); } catch (e) { toast(e && e.message ? e.message : M.failed); return; }
		modal({
			title: M.escalation, cls: "rv-hall-dlg-wide",
			body: [
				h("p", null, h("a", { href: "https://report.cybertip.org/", rel: "noopener noreferrer", "data-ext": "", text: M.cybertip })),
				h("p", { text: M.danger }),
				h("pre", { class: "rv-hall-mpre", text: JSON.stringify(j, null, 2) }),
			],
			actions: [{ label: C.CLOSE, run: (a) => a.close() }],
		});
	}

	// A room, lockdown or mode change (from here, another moderator or Discord): keep the Room tab current.
	function roomChanged() { if (isOpen() && tab === "room") paintRoom(); }

	return { toggle, close, poke, act, approve, userCard, isOpen, roomChanged };
}
